/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef } from "react"
import debounce from "lodash.debounce"

import useTracking from "hooks/useTracking"
import useProductTracking from "hooks/useProductTracking"
import { applyVideoCloudinaryTransforms } from "utils/applyCloudinaryTransforms"
import useScrollAnimation from "hooks/useScrollAnimation"
import ContentfulCard from "components/page-components/card"
import VideoCarouselModalDesktop from "./video-carousel-modal/desktop"
import VideoCarouselModalMobile from "./video-carousel-modal/mobile"

import {
  Wrapper,
  Items,
  Arrow,
  ScrollContainer,
  Grid,
  ProductCardWrapper,
  ProductCard,
  ImageVideoWrapper,
  ImageRegular,
  ImageCropped,
  MobileVideo,
  DesktopVideo,
  ExpandIcon,
  ArticleCard,
} from "./styles"

const mediaWidthDesktop = 322
const mediaHeightDesktop = 458
const mediaWidthMobile = 253
const mediaHeightMobile = 377

const ScrollableGrid = ({ items, pageType, tracking, trackingId, isScrollable }) => {
  if (items?.length === 0) {
    return null
  }

  const { itemType } = items[0]

  const refScrollable = useRef(null)
  const [currentItemIdx, setCurrentItemIdx] = useState(0)
  const [arrowLeftDisabled, setArrowLeftDisabled] = useState(true)
  const [arrowRightDisabled, setArrowRightDisabled] = useState(false)
  const [arrowLeftModalDisabled, setArrowLeftModalDisabled] = useState(false)
  const [arrowRightModalDisabled, setArrowRightModalDisabled] = useState(false)

  const [gridRef, isVisible] = useScrollAnimation({
    threshold: 0.4,
  })

  const handleArrowClick = (direction) => {
    const container = refScrollable.current
    const itemOffests = Array.from(container.children).map((child) => child.offsetLeft)

    if (direction === "left") {
      container.scrollLeft = Math.max(0, itemOffests[currentItemIdx - 1])
      setCurrentItemIdx((prev) => prev - 1)
    } else {
      container.scrollLeft = Math.min(container.scrollWidth, itemOffests[currentItemIdx + 1])
      setCurrentItemIdx((prev) => prev + 1)
    }

    if (selectedItem) {
      setSelectedItem(items[direction === "left" ? selectedItemIdx - 1 : selectedItemIdx + 1])
      const newItemIdx = currentItemIdx + (direction === "left" ? -1 : 1)
      setSelectedItemIdx(newItemIdx)
      if (newItemIdx === 0) {
        setArrowLeftModalDisabled(true)
      } else if (newItemIdx === items.length - 1) {
        setArrowRightModalDisabled(true)
      } else {
        setArrowLeftModalDisabled(false)
        setArrowRightModalDisabled(false)
      }
    }
  }

  useEffect(() => {
    const handleScroll = debounce((event) => {
      setArrowLeftDisabled(event.target.scrollLeft === 0)
      setArrowRightDisabled(
        event.target.scrollLeft + event.target.clientWidth >= event.target.scrollWidth,
      )
    }, 20)

    const container = refScrollable.current
    container.addEventListener("scroll", handleScroll)

    return () => container.removeEventListener("scroll", handleScroll)
  }, [])

  const { trackRender, trackView, trackClick } = useTracking()
  const { trackProductListView } = useProductTracking()
  const ref = useRef()

  if (tracking && tracking.includes("Render")) {
    trackRender(ref, trackingId, { ...(pageType && { page_type: pageType }) })
  }

  if (tracking && tracking.includes("View")) {
    if (itemType === "Product") {
      trackProductListView({
        elementRef: ref,
        trackingId,
        items,
      })
    } else {
      trackView(ref, trackingId, { ...(pageType && { page_type: pageType }) })
    }
  }

  if (tracking && tracking.includes("Render")) {
    trackClick(ref, trackingId, { ...(pageType && { page_type: pageType }) })
  }

  const [selectedItem, setSelectedItem] = useState(null)
  const [selectedItemIdx, setSelectedItemIdx] = useState(-1)
  const refModalDesktop = useRef(null)

  const handleOpenModalClick = (item, idx) => {
    setSelectedItem(item)
    setSelectedItemIdx(idx)
    setArrowLeftModalDisabled(idx === 0)
    setArrowRightModalDisabled(idx === items.length - 1)
    document.body.style.overflow = "hidden"

    // manually prevent autoplay on desktop modal when mobile modal is open;
    // desktop modal is hidden at that point and should not play the video
    // 'display: none' doesn't prevent the video from autoplaying
    setTimeout(() => {
      const modalDesktop = refModalDesktop.current
      if (modalDesktop) {
        const video = modalDesktop.querySelector("video")
        if (getComputedStyle(modalDesktop).display === "none") {
          video.pause()
        }
      }
    }, 0)
  }
  const handleCloseModalClick = () => {
    setSelectedItem(null)
    setSelectedItemIdx(-1)
    document.body.style.overflow = "unset"
  }

  const renderProductCard = (item, idx) => {
    const refCard = useRef(null)

    const handleCardFocus = () => {
      if (refCard.current) {
        refCard.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        })
      }
    }

    return (
      <ProductCardWrapper
        as="li"
        ref={refCard}
        key={`${item.shopifyProductVariant?.variantId}-${idx}`}
        className={isVisible && isScrollable ? "animate-in" : ""}
        animationIndex={idx}
        onFocus={handleCardFocus}
        tabIndex={-1}
      >
        <ProductCard {...item} />
        {/* <ImageAndVideo
          refCard={refCard}
          imageUrl={item.image && item.image.length && item.image[0].secure_url}
          imageCrops={item.imageCrops}
          video={item.previewVideo}
          onExpandClick={() => handleOpenModalClick(item, idx)}
        /> */}
      </ProductCardWrapper>
    )
  }

  return (
    <>
      <Wrapper isScrollable={isScrollable}>
        <Items ref={ref}>
          {/* <Scroll itemType={itemType} isScrollable={isScrollable} /> */}
          <ScrollContainer>
            <Grid
              as="ul"
              role="list"
              ref={(el) => {
                refScrollable.current = el
                gridRef.current = el
              }}
            >
              <Arrow
                direction="left"
                onClick={() => handleArrowClick("left")}
                disabled={arrowLeftDisabled}
                isScrollable={isScrollable}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !arrowLeftDisabled) {
                    e.preventDefault()
                    handleArrowClick("left")
                  }
                }}
              />
              <Arrow
                isScrollable={isScrollable}
                direction="right"
                onClick={() => handleArrowClick("right")}
                disabled={arrowRightDisabled}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !arrowRightDisabled) {
                    e.preventDefault()
                    handleArrowClick("right")
                  }
                }}
              />
              {items.map((item, idx) =>
                item.itemType === "Product" ? (
                  renderProductCard(item, idx)
                ) : item.itemType === "Article" ? (
                  <ArticleCard
                    as="li"
                    key={`${item.id}-${idx}`}
                    pageType={pageType}
                    isGrid
                    article={item.article}
                  />
                ) : item.itemType === "Podcast" ? (
                  <ContentfulCard
                    as="li"
                    key={`${item.id}-${idx}`}
                    component={item}
                    isGrid
                    fullHeight
                    isScrollable
                  />
                ) : null,
              )}
            </Grid>
          </ScrollContainer>
        </Items>
      </Wrapper>
      {selectedItem && (
        <>
          <VideoCarouselModalDesktop
            elRef={refModalDesktop}
            {...selectedItem}
            onArrowClick={handleArrowClick}
            arrowLeftDisabled={arrowLeftModalDisabled}
            arrowRightDisabled={arrowRightModalDisabled}
            onCloseModal={handleCloseModalClick}
          />
          <VideoCarouselModalMobile
            items={items}
            idx={selectedItemIdx}
            onCloseModal={handleCloseModalClick}
          />
        </>
      )}
    </>
  )
}

const ImageAndVideo = ({ refCard, imageUrl, imageCrops, video, onExpandClick }) => {
  if (!video) {
    return null
  }

  const refImage = useRef(null)
  const refVideo = useRef(null)

  useEffect(() => {
    const handlePlay = () => {
      if (refImage.current && refVideo.current) {
        refVideo.current.style.opacity = 1
        refImage.current.style.opacity = 0
        refVideo.current.play()
      }
    }

    const handlePause = () => {
      if (refImage.current && refVideo.current) {
        refVideo.current.pause()
      }
    }

    if (refCard.current) {
      refCard.current.addEventListener("mouseenter", handlePlay)
      refCard.current.addEventListener("mouseleave", handlePause)
    }

    return () => {
      if (refCard.current) {
        refCard.current.removeEventListener("mouseenter", handlePlay)
        refCard.current.removeEventListener("mouseleave", handlePause)
      }
    }
  }, [])

  const imageElement = imageUrl ? (
    <ImageRegular
      elRef={refImage}
      variant="4col"
      baseUrl={imageUrl}
      images={[
        {
          screenSize: 576,
          transforms: `c_fit,w_${mediaWidthDesktop},h_${mediaHeightDesktop},q_90,fl_lossy,f_auto`,
        },
        {
          transforms: `c_fill,g_auto,w_${mediaWidthMobile},h_${mediaHeightMobile},q_90,f_auto,fl_lossy`,
        },
      ]}
      lazy
      isGrid
      imagePosition="top"
    />
  ) : imageCrops ? (
    <ImageCropped
      elRef={refImage}
      variant="4col"
      imageData={imageCrops}
      sizes={[
        {
          query: 576,
          width: mediaWidth,
          height: mediaHeight,
          transforms: `c_fill,g_auto,w_${mediaWidthDesktop},h_${mediaHeightDesktop},q_90,f_auto,fl_lossy`,
        },
        {
          query: 0,
          width: mediaWidth,
          height: mediaHeight,
          transforms: `c_fill,g_auto,w_${mediaWidthMobile},h_${mediaHeightMobile},q_90,f_auto,fl_lossy`,
        },
      ]}
      lazy
      isGrid
      imagePosition="top"
    />
  ) : null

  const videoProps = {
    ref: refVideo,
    width: "100%",
    muted: true,
    loop: video.loopVideo,
    playsInline: true,
    type: "video/mp4",
    src: applyVideoCloudinaryTransforms(
      video.videoObject[0].secure_url,
      `ac_none,c_fill,w_${mediaWidthDesktop},h_${mediaHeightDesktop}`,
    ),
  }

  const videoElement = (
    <>
      <MobileVideo {...videoProps} autoPlay loop />
      <DesktopVideo {...videoProps} />
    </>
  )

  return (
    <ImageVideoWrapper onClick={onExpandClick}>
      {imageElement}
      {videoElement}
      <ExpandIcon id="arrow-open" width={20} height={20} />
    </ImageVideoWrapper>
  )
}

export default ScrollableGrid
